<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { onClickOutside } from '@vueuse/core'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { useUserStore } from '@/authentication/user.store'
import {
  getAdminSchools,
  getTeachersForSchool,
  getAllSchoolsTeachers,
  getNoSchoolAssignedTeachers,
  downloadTeachersCSV,
  type School,
  type TeachersSegmented
} from './admin-api'
import ProgressSpinner from 'primevue/progressspinner'
import GbInlineMessage from '@/components/GbInlineMessage.vue'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const analytics = getAnalyticsTracker()

const schools = ref<School[]>([])
const currentSchool = ref<School | null>(null)
const teachers = ref<TeachersSegmented>({
  active_enterprise: [],
  active_trial: [],
})
const loading = ref(true)
const error = ref<string>('')
const isDomainAdmin = ref(false)
const domainName = ref<string>('')
const superAdminDomainUuid = ref<string>('')

const { isSuperAdmin, userDomainUuid, domain_info } = storeToRefs(userStore)

const showActivation = computed(() => (domain_info.value as { show_tk_activation: boolean })?.show_tk_activation ?? false)

// Use super admin domain UUID if available, otherwise use user's domain UUID
const effectiveDomainUuid = computed(() => 
  superAdminDomainUuid.value || userDomainUuid.value
)

const schoolUuid = computed(() => route.params.schoolUuid as string || '')

const totalUsers = computed(() => {
  const lists = [teachers.value.active_enterprise, teachers.value.active_trial]
  return lists.reduce((sum, list) => sum + list.length, 0)
})

const isAllSchools = computed(() => schoolUuid.value === 'all_schools')

const isDownloadDropdownOpen = ref(false)
const downloadDropdownRef = ref<HTMLElement | null>(null)

const displaySchoolName = computed(() => {
  if (!currentSchool.value) return ''
  if (currentSchool.value.uuid === 'all_schools') return 'All Schools'
  if (currentSchool.value.uuid === 'no_school_assigned') return 'No School Assigned'
  return currentSchool.value.name
})

// Close dropdown when clicking outside
onClickOutside(downloadDropdownRef, () => {
  isDownloadDropdownOpen.value = false
})

async function loadSchools() {
  try {
    // Get analytics_domain_uuid from URL query parameters if present
    const analyticsDomainUuid = route.query.analytics_domain_uuid as string | undefined
    
    const response = await getAdminSchools(analyticsDomainUuid)
    schools.value = response.schools
    isDomainAdmin.value = response.is_domain_admin
    superAdminDomainUuid.value = response.super_admin_domain_uuid || ''
    
    // If no school selected, redirect to first school or all_schools
    if (!schoolUuid.value) {
      if (response.super_admin_domain_uuid || isDomainAdmin.value) {
        router.push({ name: 'AdminUserList', params: { schoolUuid: 'all_schools' } })
      } else if (schools.value.length > 0) {
        router.push({ name: 'AdminUserList', params: { schoolUuid: schools.value[0].uuid } })
      }
    }
  } catch (e) {
    error.value = 'Failed to load schools. You may not have admin permissions.'
    console.error('Error loading admin schools:', e)
    loading.value = false
  }
}

async function loadTeachers() {
  if (!schoolUuid.value) return
  
  loading.value = true
  error.value = ''
  
  try {
    let response
    if (schoolUuid.value === 'all_schools') {
      response = await getAllSchoolsTeachers(effectiveDomainUuid.value)
    } else if (schoolUuid.value === 'no_school_assigned') {
      response = await getNoSchoolAssignedTeachers(effectiveDomainUuid.value)
    } else {
      response = await getTeachersForSchool(effectiveDomainUuid.value, schoolUuid.value)
    }
    
    currentSchool.value = schools.value.find(s => s.uuid === schoolUuid.value) || { uuid: response.school_uuid, name: response.school_uuid }
    teachers.value = response.teachers
    if (response.domain) {
      domainName.value = response.domain.name
    }
    
    analytics.sendEvent('Viewed Admin Toolkit User List', {
      school_uuid: schoolUuid.value,
      school_name: currentSchool.value.name,
      domain_uuid: effectiveDomainUuid.value,
      domain_name: domainName.value,
      is_all_schools: schoolUuid.value === 'all_schools',
      is_no_school_assigned: schoolUuid.value === 'no_school_assigned'
    })
  } catch (e) {
    error.value = 'Failed to load teachers data'
    console.error('Error loading teachers:', e)
  } finally {
    loading.value = false
  }
}

function toggleDownloadDropdown() {
  isDownloadDropdownOpen.value = !isDownloadDropdownOpen.value
}

function handleDownloadCSV(sortedBy?: string) {
  if (schoolUuid.value) {        
    downloadTeachersCSV(effectiveDomainUuid.value, schoolUuid.value, sortedBy)
    analytics.sendEvent('Downloaded Admin Toolkit User List', {
      school_uuid: schoolUuid.value,
      school_name: currentSchool.value?.name || schoolUuid.value,
      domain_uuid: effectiveDomainUuid.value,
      domain_name: domainName.value,
      is_all_schools: schoolUuid.value === 'all_schools'
    })
  }
  isDownloadDropdownOpen.value = false
}

function switchSchool(uuid: string) {
  router.push({ name: 'AdminUserList', params: { schoolUuid: uuid } })
}

onMounted(async () => {
  await loadSchools()
  await loadTeachers()
})

// Reload teachers when school changes
const unwatchRoute = router.afterEach((to, from) => {
  if (to.params.schoolUuid !== from.params.schoolUuid) {
    loadTeachers()
  }
})

onUnmounted(() => {
  unwatchRoute()
})
</script>

<template>
  <div class="px-6 pt-12 pb-20">
    <div class="max-w-7xl mx-auto">
      <h1 class="text-center text-display-xl font-normal leading-[60px] mb-10">Toolkit User List</h1>

      <div v-if="isSuperAdmin" class="text-center text-sm text-gray-700 mb-8">
        <span class="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
          <span class="material-icons-round text-xs text-blue-600">admin_panel_settings</span>
          <span class="font-medium">Super Admin</span>
          <span class="text-gray-600">·</span>
          <span>{{ domainName }}</span>
        </span>
      </div>

      <!-- Not Admin Error -->
      <div v-if="error && !loading" class="flex items-center justify-center min-h-[400px]">
        <GbInlineMessage gbTheme="red" class="max-w-2xl">
          <template #icon></template>
          <div class="inline-flex items-center">
            <span class="material-icons-round text-red-300 mr-2 self-start">warning_amber</span>
            <div class="space-y-1">
              <p class="font-bold text-base leading-6">Access Denied</p>
              <p class="text-sm leading-5">
                You must be a school or district administrator to view this page.
                If you believe you are viewing this in error, please contact us at <strong>support@goalbook.com</strong>.
              </p>
            </div>
          </div>
        </GbInlineMessage>
      </div>

      <!-- Loading State -->
      <div v-else-if="loading" class="flex items-center justify-center min-h-[400px]">
        <div class="text-center">
          <ProgressSpinner class="mb-4 h-8 w-8 stroke-primary-500" strokeWidth="4" />
          <p class="text-gray-700 font-medium">Loading user data...</p>
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- School Selector -->
        <div class="p-4 mb-6">
          <label for="school-selector" class="text-display-xs font-semibold text-gray-900 mr-3">School:</label>
          <select
            id="school-selector"
            :value="schoolUuid" 
            @change="switchSchool(($event.target as HTMLSelectElement).value)"
            class="border border-gray-300 rounded-md px-3 py-2 min-w-[250px] text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option 
              v-for="school in schools" 
              :key="school.uuid" 
              :value="school.uuid"
            >
              {{ school.name }}
            </option>
          </select>
        </div>

        <!-- School Header with Download -->
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-2xl font-semibold text-gray-900">
            {{ displaySchoolName }}
            <span class="text-gray-700 text-lg font-normal ml-2">({{ totalUsers }} users)</span>
          </h2>

          <!-- Download CSV -->
          <div v-if="isAllSchools" ref="downloadDropdownRef" class="relative inline-block">
            <button
              class="bg-primary-600 text-white border border-primary-700 rounded-md px-4 py-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center gap-2 font-medium shadow-sm transition-colors"
              @click="toggleDownloadDropdown"
            >
              <span class="material-icons-round text-base">download</span>
              Download CSV
              <span class="material-icons-round text-base">arrow_drop_down</span>
            </button>
            <div 
              v-show="isDownloadDropdownOpen"
              class="absolute right-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-10 min-w-[200px]"
            >
              <button 
                @click="handleDownloadCSV()"
                class="block w-full text-left px-4 py-2 text-gray-900 hover:bg-gray-100 first:rounded-t-md transition-colors"
              >
                Sorted by name
              </button>
              <button 
                @click="handleDownloadCSV('school')"
                class="block w-full text-left px-4 py-2 text-gray-900 hover:bg-gray-100 last:rounded-b-md transition-colors"
              >
                Sorted by school
              </button>
            </div>
          </div>
          <button 
            v-else
            @click="handleDownloadCSV()"
            class="bg-primary-600 text-white border border-primary-700 rounded-md px-4 py-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center gap-2 font-medium shadow-sm transition-colors"
          >
            <span class="material-icons-round text-base">download</span>
            Download CSV
          </button>
        </div>

        <!-- Teacher Tables -->
        <div v-for="(teacherList, listType) in [
          { list: teachers.active_enterprise, title: 'Current Enterprise Users' },
          { list: teachers.active_trial, title: 'Current Trial Users' }
        ]" :key="listType">
          <div v-if="teacherList.list.length > 0" class="mb-8">
            <h3 class="text-xl font-semibold text-gray-900 mb-4">
              {{ teacherList.title }}
              <span class="text-gray-700 text-base font-normal ml-2">({{ teacherList.list.length }} users)</span>
            </h3>

            <div class="overflow-x-auto border border-gray-300 rounded-lg shadow-sm">
              <table class="w-full">
                <thead class="bg-gray-100 border-b border-gray-300">
                  <tr>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Teacher Name</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Email</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Title</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Subscription Type</th>
                    <th v-if="showActivation" class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Activated</th>
                    <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900">End Date</th>
                    <th v-if="isAllSchools" class="px-4 py-3 text-left text-sm font-semibold text-gray-900">Schools</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr 
                    v-for="teacher in teacherList.list" 
                    :key="teacher.uuid"
                    class="hover:bg-gray-50 transition-colors"
                  >
                    <td class="px-4 py-3 text-sm text-gray-900 font-medium">{{ teacher.name }}</td>
                    <td class="px-4 py-3 text-sm text-gray-700">{{ teacher.email }}</td>
                    <td class="px-4 py-3 text-sm text-gray-700">{{ teacher.title || '-' }}</td>
                    <td class="px-4 py-3 text-sm">
                      <span v-if="teacher.type === 'enterprise' && teacher.sub_type !== 'trial'" class="inline-flex items-center gap-1">
                        <span class="text-gray-900 font-medium">Enterprise</span>
                        <span v-if="isSuperAdmin" class="text-gray-600 text-xs">({{ teacher.sub_type }})</span>
                      </span>
                      <span v-else class="text-gray-900 font-medium">Trial</span>
                    </td>
                    <td v-if="showActivation" class="px-4 py-3 text-sm text-gray-700">{{ teacher.activated ? 'Yes' : 'No' }}</td>
                    <td class="px-4 py-3 text-sm text-gray-700">{{ teacher.end_date?.split('T')[0] || '-' }}</td>
                    <td v-if="isAllSchools" class="px-4 py-3 text-sm text-gray-700">{{ teacher.schools?.join(', ') || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- No Users Message -->
        <div v-if="totalUsers === 0" class="text-center py-16">
          <p class="text-gray-700 text-lg font-medium">No users found for this school.</p>
        </div>
      </div>
    </div>
  </div>
</template>
